import React, { createContext, useContext, useState, useEffect } from "react";
import api from "../../services/APIService";
import { getUserInfo } from "../../utils/AuthManager";

const SearchContext = createContext();

export const SearchProvider = ({ children }) => {
  const STORAGE_KEY = "searchParams";
  const TIMESTAMP_KEY = "searchParamsTimestamp";
  const HALF_HOUR = 30 * 60 * 1000; // 半小時的毫秒數

  // 菜單配置緩存（用於動態查找商品頁面路徑）
  const [menuConfig, setMenuConfig] = useState(null);

  // const [searchParams, setSearchParams] = useState(() => {
  //   // 初始化時從 localStorage 加載數據
  //   const savedParams = localStorage.getItem(STORAGE_KEY);
  //   return savedParams ? JSON.parse(savedParams) : {};
  // });
  const [searchParams, setSearchParams] = useState(() => {
    // 初始化時從 localStorage 加載數據
    const savedParams = localStorage.getItem(STORAGE_KEY);
    const savedTimestamp = localStorage.getItem(TIMESTAMP_KEY);

    // 檢查是否超過半小時
    if (
      savedTimestamp &&
      Date.now() - parseInt(savedTimestamp, 10) > HALF_HOUR
    ) {
      localStorage.removeItem(STORAGE_KEY); // 清空過期的數據
      localStorage.removeItem(TIMESTAMP_KEY);
      return {};
    }

    return savedParams ? JSON.parse(savedParams) : {};
  });

  // 在 searchParams 更新時同步到 localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(searchParams));
    localStorage.setItem(TIMESTAMP_KEY, Date.now().toString()); // 同步更新時間戳
  }, [searchParams]);

  // const updateSearchParams = (pageKey, params) => {
  //   setSearchParams((prev) => ({
  //     ...prev,
  //     [pageKey]: { ...prev[pageKey], ...params },
  //   }));
  // };
  const updateSearchParams = (pageKey, params) => {
    setSearchParams(() => ({
      [pageKey]: {
        ...(searchParams[pageKey] || {}),
        ...params,
      },
    }));
  };

  const getSearchParams = (pageKey) => searchParams[pageKey] || {};

  const [refreshKey, setRefreshKey] = useState(0); // 用于刷新控制的key

  const triggerRefresh = () => setRefreshKey((prev) => prev + 1); // 每次触发时改变key值

  // ==================== 動態獲取商品頁面路徑 ====================
  /**
   * 獲取商品頁面的路徑（從菜單配置中動態查找）
   * @returns {Promise<string>} 商品頁面的完整路徑，例如 "/DefaultPage/Prod"
   */
  const getProductOverviewPath = async () => {
    try {
      // 如果已經有緩存的菜單配置，直接使用
      if (menuConfig && menuConfig.menu) {
        const productMenuItem = menuConfig.menu.find(
          (item) => item.hyperLink === "/ProductOverview",
        );

        if (productMenuItem && productMenuItem.menuID) {
          return `/DefaultPage/${productMenuItem.menuID}`;
        }
      }

      // 如果沒有緩存，從 API 獲取
      const response = await api.defaultPageApi.defaultPageGetMenuGet();
      const result = response.data;

      // 緩存菜單配置
      setMenuConfig(result);

      // 查找商品頁面選項
      const menu = result?.menu || [];
      const productMenuItem = menu.find(
        (item) => item.hyperLink === "/ProductOverview",
      );

      if (productMenuItem && productMenuItem.menuID) {
        return `/DefaultPage/${productMenuItem.menuID}`;
      }

      // 如果找不到，返回預設路徑
      console.warn("找不到商品頁面選項，使用預設路徑 /DefaultPage/Prod");
      return "/DefaultPage/Prod";
    } catch (error) {
      console.error("獲取商品頁面路徑失敗:", error);
      // 發生錯誤時返回預設路徑
      return "/DefaultPage/Prod";
    }
  };

  // ==================== 檢查是否有未完成付款的訂單 ====================
  /**
   * 檢查是否有暫存狀態（"T"）的未完成付款訂單
   * 不限制當天，只要是暫存狀態就需要攔截。
   * 超過保留天數的驗證交由後端在重新付款時處理（避免前端重複維護設定）。
   * @returns {Promise<Object>} { hasPendingOrder: boolean, orders: Array }
   */
  const checkTodayUnpaidOrders = async () => {
    try {
      const userInfo = getUserInfo();
      if (!userInfo || !userInfo.memberNo) {
        return { hasPendingOrder: false, orders: [] };
      }

      // 呼叫 API 查詢會員最新的未付款訂單（後端只返回最新一筆暫存訂單）
      const result =
        await api.productOverviewApi.productOverviewGetPendingOrderGet({
          memberNo: userInfo.memberNo,
        });

      if (!result.success || !result.data) {
        return { hasPendingOrder: false, orders: [] };
      }

      // 後端返回單一訂單物件（不是陣列）
      const order = result.data;

      // 不再限制「當天」，保留期限超過與否交由重新付款時的後端驗證決定
      return {
        hasPendingOrder: true,
        orders: [order],
        // 保留舊欄位供相容（deprecated，請改用 hasPendingOrder）
        hasTodayUnpaid: true,
      };
    } catch (error) {
      console.error("檢查未完成付款訂單失敗:", error);
      return { hasPendingOrder: false, orders: [] };
    }
  };

  // ==================== 檢查優惠券是否過期 ====================
  /**
   * 檢查優惠券是否在有效期限內
   * @param {Object} coupon - 優惠券物件（需包含促銷活動編號）
   * @returns {Promise<Object>} { isValid: boolean, message: string, expiryDate: string }
   */
  const checkCouponExpiry = async (coupon) => {
    try {
      if (!coupon || !coupon.Id) {
        return {
          isValid: false,
          message: "優惠券資料不完整",
          expiryDate: null,
        };
      }

      // 呼叫 API 查詢優惠券詳細資料
      const result =
        await api.productOverviewApi.productOverviewGetPromotionDiscountGet({
          promotionDiscountNo: coupon.Id,
        });

      if (!result.success || !result.data) {
        return {
          isValid: false,
          message: "查詢優惠券失敗",
          expiryDate: null,
        };
      }

      const promotionData = result.data;

      // 檢查優惠券結束日期
      const now = new Date();
      const endDate = new Date(
        promotionData.PromotionsDateE || promotionData.promotionsDateE,
      );

      if (isNaN(endDate.getTime())) {
        return {
          isValid: false,
          message: "優惠券日期格式錯誤",
          expiryDate: null,
        };
      }

      // 判斷是否過期
      const isExpired = now > endDate;

      return {
        isValid: !isExpired,
        message: isExpired
          ? `優惠券已於 ${endDate.toLocaleDateString("zh-TW")} 過期`
          : "優惠券有效",
        expiryDate: endDate.toISOString(),
        promotionData: promotionData,
      };
    } catch (error) {
      console.error("檢查優惠券有效期失敗:", error);
      return {
        isValid: false,
        message: "檢查優惠券失敗",
        expiryDate: null,
      };
    }
  };

  // ==================== 批次檢查購物車優惠券 ====================
  /**
   * 批次檢查購物車中所有商品的優惠券是否過期
   * @param {Array} cartItems - 購物車商品陣列
   * @param {Object|null} orderCoupon - 訂單層級優惠券（可選）
   * @param {boolean} autoRemove - 是否自動移除過期優惠券（預設 false）
   * @returns {Promise<Object>} { allValid: boolean, itemExpiredCoupons: Array, orderExpiredCoupon: Object|null, cleanedItems: Array }
   */
  const checkCartCouponsExpiry = async (
    cartItems,
    orderCoupon = null,
    autoRemove = false,
  ) => {
    try {
      if (!cartItems || cartItems.length === 0) {
        return {
          allValid: true,
          itemExpiredCoupons: [],
          orderExpiredCoupon: null,
          cleanedItems: cartItems,
        };
      }

      // 收集所有優惠券 ID（包含商品優惠券和訂單優惠券）
      const couponIds = [];
      const couponMap = new Map(); // 用於快速查找商品資訊

      for (const item of cartItems) {
        if (item.appliedCoupon && item.appliedCoupon.Id) {
          couponIds.push(item.appliedCoupon.Id);
          couponMap.set(item.appliedCoupon.Id, {
            productName: item.name,
            couponCode: item.appliedCoupon.DiscountCode,
            couponName: item.appliedCoupon.DiscountName,
            type: "商品優惠券",
            cartItemNo: item.cartItemNo,
            quantity: item.quantity,
          });
        }
      }

      // 加入訂單優惠券
      if (orderCoupon && orderCoupon.Id) {
        couponIds.push(orderCoupon.Id);
        couponMap.set(orderCoupon.Id, {
          productName: "訂單折扣",
          couponCode: orderCoupon.DiscountCode,
          couponName: orderCoupon.DiscountName,
          type: "訂單優惠券",
        });
      }

      if (couponIds.length === 0) {
        return {
          allValid: true,
          itemExpiredCoupons: [],
          orderExpiredCoupon: null,
          cleanedItems: cartItems,
        };
      }

      // 呼叫批次檢查 API（一次性檢查所有優惠券，去重後送出避免同張多件優惠券重複回傳）
      const result =
        await api.productOverviewApi.productOverviewCheckCouponExpiryPost({
          checkCouponExpiryRequest: {
            couponIds: [...new Set(couponIds)], // 去重，避免同張優惠券多規格重複計入
          },
        });

      if (!result.success || !result.data) {
        // API 失敗時回退到逐個檢查
        console.warn("批次檢查 API 失敗，回退到逐個檢查");
        return await checkCartCouponsExpiryFallback(
          cartItems,
          orderCoupon,
          autoRemove,
        );
      }

      const itemExpiredCoupons = [];
      let orderExpiredCoupon = null;
      const expiredCouponIds = new Set();

      (result.data.ExpiredCoupons || []).forEach((expired) => {
        const couponId = expired.CouponId || expired.couponId; // 後端返回 PascalCase
        const itemInfo = couponMap.get(couponId);

        expiredCouponIds.add(couponId);

        const expiredInfo = {
          couponId: couponId,
          productName: itemInfo?.productName || "未知商品",
          couponCode: expired.CouponCode || expired.couponCode || "", // 後端返回 PascalCase
          couponName: expired.CouponName || expired.couponName || "", // 後端返回 PascalCase
          message: expired.Message || expired.message || "優惠券已過期", // 後端返回 PascalCase
          type: itemInfo?.type || "未知類型",
          cartItemNo: itemInfo?.cartItemNo, // 加入 cartItemNo 用於後續刪除
        };

        if (itemInfo?.type === "訂單優惠券") {
          orderExpiredCoupon = expiredInfo;
        } else {
          itemExpiredCoupons.push(expiredInfo);
        }
      });

      // 如果需要自動移除過期優惠券
      let cleanedItems = cartItems;
      if (autoRemove && expiredCouponIds.size > 0) {
        const userInfo = getUserInfo();
        if (userInfo && userInfo.memberNo) {
          // ✅ 修改：只清除優惠券，不刪除商品
          const removePromises = cartItems.map(async (item) => {
            if (
              item.appliedCoupon &&
              expiredCouponIds.has(item.appliedCoupon.Id)
            ) {
              console.warn(
                `🧹 自動清除商品 ${item.name} 的過期優惠券（${item.appliedCoupon.DiscountName}）`,
              );

              if (item.cartItemNo) {
                try {
                  const updateResult =
                    await api.productOverviewApi.productOverviewUpdateCartItemCouponPut(
                      {
                        updateCartCouponRequest: {
                          cartItemNo: item.cartItemNo,
                          promotionsDiscountNo: null, // null = 移除優惠券
                        },
                      },
                    );

                  if (!updateResult.success) {
                    console.error(
                      `後端清除優惠券失敗：${updateResult.message || "未知錯誤"}`,
                    );
                  }
                } catch (error) {
                  console.error(
                    `❌ 清除購物車優惠券失敗 (CartItemNo: ${item.cartItemNo}):`,
                    error,
                  );
                }
              } else {
                console.warn(
                  `⚠️ 商品 ${item.name} 沒有 cartItemNo，無法同步資料庫`,
                );
              }

              // ✅ 無論後端成功與否，前端都清除 appliedCoupon，商品本身保留
              return { ...item, appliedCoupon: null, productDiscount: null };
            }
            return item; // 保留沒有過期優惠券的商品
          });

          cleanedItems = await Promise.all(removePromises);
          // 注意：不再 filter(null)，所有商品都保留
        }
      }

      return {
        allValid: (result.data.AllValid ?? result.data.allValid) === true,
        itemExpiredCoupons: itemExpiredCoupons,
        orderExpiredCoupon: orderExpiredCoupon,
        cleanedItems: cleanedItems,
      };
    } catch (error) {
      console.error("批次檢查購物車優惠券失敗:", error);
      // 發生錯誤時回退到逐個檢查
      return await checkCartCouponsExpiryFallback(
        cartItems,
        orderCoupon,
        autoRemove,
      );
    }
  };

  // ==================== 回退方案：逐個檢查優惠券 ====================
  /**
   * 當批次 API 失敗時的回退方案
   * @param {Array} cartItems - 購物車商品陣列
   * @param {Object|null} orderCoupon - 訂單層級優惠券（可選）
   * @param {boolean} autoRemove - 是否自動移除過期優惠券
   * @returns {Promise<Object>}
   */
  const checkCartCouponsExpiryFallback = async (
    cartItems,
    orderCoupon = null,
    autoRemove = false,
  ) => {
    try {
      const itemExpiredCoupons = [];
      let orderExpiredCoupon = null;
      const checkPromises = [];

      // 檢查商品優惠券
      for (const item of cartItems) {
        if (item.appliedCoupon && item.appliedCoupon.Id) {
          checkPromises.push(
            checkCouponExpiry(item.appliedCoupon).then((result) => ({
              item: item,
              coupon: item.appliedCoupon,
              checkResult: result,
              type: "商品優惠券",
            })),
          );
        }
      }

      // 檢查訂單優惠券
      if (orderCoupon && orderCoupon.Id) {
        checkPromises.push(
          checkCouponExpiry(orderCoupon).then((result) => ({
            item: null,
            coupon: orderCoupon,
            checkResult: result,
            type: "訂單優惠券",
          })),
        );
      }

      const results = await Promise.all(checkPromises);
      const expiredCouponIds = new Set();

      // 篩選出過期的優惠券
      results.forEach(({ item, coupon, checkResult, type }) => {
        if (!checkResult.isValid) {
          expiredCouponIds.add(coupon.Id);

          const expiredInfo = {
            couponId: coupon.Id,
            productName: item ? item.name : "訂單折扣",
            couponCode: coupon.DiscountCode,
            couponName: coupon.DiscountName,
            message: checkResult.message,
            type: type,
          };

          if (type === "訂單優惠券") {
            orderExpiredCoupon = expiredInfo;
          } else {
            itemExpiredCoupons.push(expiredInfo);
          }
        }
      });

      // 如果需要自動移除過期優惠券
      let cleanedItems = cartItems;
      if (autoRemove && expiredCouponIds.size > 0) {
        const userInfo = getUserInfo();
        if (userInfo && userInfo.memberNo) {
          const updatePromises = cartItems.map(async (item) => {
            if (
              item.appliedCoupon &&
              expiredCouponIds.has(item.appliedCoupon.Id)
            ) {
              console.warn(`回退方案：自動移除商品 ${item.name} 的過期優惠券`);

              if (item.cartItemNo) {
                try {
                  const updateResult =
                    await api.productOverviewApi.productOverviewUpdateCartItemQuantityPut(
                      {
                        updateCartItemRequest: {
                          cartItemNo: item.cartItemNo,
                          quantity: item.quantity,
                          promotionsDiscountNo: null,
                        },
                      },
                    );

                  if (!updateResult.success) {
                    console.error(
                      `後端移除優惠券失敗：${updateResult.message || "未知錯誤"}`,
                    );
                  }
                } catch (error) {
                  console.error(
                    `❌ [回退方案] 更新購物車失敗 (CartItemNo: ${item.cartItemNo}):`,
                    error,
                  );
                }
              } else {
                console.warn(
                  `⚠️ [回退方案] 商品 ${item.name} 沒有 cartItemNo，無法更新後端資料庫`,
                );
              }

              return {
                ...item,
                appliedCoupon: null,
                promotionsDiscountNo: null,
              };
            }
            return item;
          });

          cleanedItems = await Promise.all(updatePromises);
        }
      }

      return {
        allValid:
          itemExpiredCoupons.length === 0 && orderExpiredCoupon === null,
        itemExpiredCoupons: itemExpiredCoupons,
        orderExpiredCoupon: orderExpiredCoupon,
        cleanedItems: cleanedItems,
      };
    } catch (error) {
      console.error("回退檢查失敗:", error);
      return {
        allValid: false,
        itemExpiredCoupons: [],
        orderExpiredCoupon: null,
        cleanedItems: cartItems,
      };
    }
  };

  // ==================== 檢查商品庫存 ====================
  /**
   * 檢查單一商品規格的庫存數量
   * @param {string} materialNo - 商品編號
   * @param {string} colorNo - 商品顏色編號
   * @param {string} specNo - 商品規格編號
   * @returns {Promise<Object>} { hasStock: boolean, availableStock: number, message: string }
   */
  const checkProductStock = async (materialNo, colorNo, specNo) => {
    try {
      if (!materialNo || !colorNo || !specNo) {
        return {
          hasStock: false,
          availableStock: 0,
          message: "商品規格資料不完整",
        };
      }

      // 查詢商品規格庫存
      const result =
        await api.productOverviewApi.productOverviewGetMaterialSpecificationGet(
          {
            materialNo: materialNo,
            colorNo: colorNo,
            specNo: specNo,
          },
        );

      if (!result.success || !result.data) {
        return {
          hasStock: false,
          availableStock: 0,
          message: "查詢商品規格失敗",
        };
      }

      const stock = result.data.Stock ?? 0;

      return {
        hasStock: stock > 0,
        availableStock: stock,
        message: stock > 0 ? `庫存充足（剩餘 ${stock} 件）` : "商品已售完",
      };
    } catch (error) {
      console.error("檢查商品庫存失敗:", error);
      return {
        hasStock: false,
        availableStock: 0,
        message: "檢查庫存時發生錯誤",
      };
    }
  };

  // ==================== 檢查訂單所有商品庫存 ====================
  /**
   * 批次檢查訂單中所有商品的庫存是否充足
   * @param {Array} orderItems - 訂單商品陣列（需包含 materialNo, colorNo, specNo, quantity）
   * @returns {Promise<Object>} { allInStock: boolean, outOfStockItems: Array, details: Array }
   */
  const checkOrderProductsStock = async (orderItems) => {
    try {
      if (!orderItems || orderItems.length === 0) {
        return {
          allInStock: true,
          outOfStockItems: [],
          details: [],
        };
      }

      // 批次查詢所有商品規格庫存
      const checkPromises = orderItems.map(async (item) => {
        const stockResult = await checkProductStock(
          item.MaterialNo ||
            item.materialNo ||
            item.ProductNo ||
            item.productNo,
          item.ColorNo || item.colorNo,
          item.SpecNo || item.specNo,
        );

        const requiredQuantity = item.Quantity || item.quantity || 0;
        const isStockSufficient =
          stockResult.availableStock >= requiredQuantity;

        return {
          productName:
            item.ProductName ||
            item.productName ||
            item.Name ||
            item.name ||
            "未知商品",
          colorName:
            item.ColorName || item.colorName || item.Color || item.color || "",
          specName:
            item.SpecName || item.specName || item.Size || item.size || "",
          requiredQuantity: requiredQuantity,
          availableStock: stockResult.availableStock,
          isStockSufficient: isStockSufficient,
          message: isStockSufficient
            ? "庫存充足"
            : `庫存不足（需要 ${requiredQuantity} 件，剩餘 ${stockResult.availableStock} 件）`,
        };
      });

      const results = await Promise.all(checkPromises);
      const outOfStockItems = results.filter((item) => !item.isStockSufficient);

      return {
        allInStock: outOfStockItems.length === 0,
        outOfStockItems: outOfStockItems,
        details: results,
      };
    } catch (error) {
      console.error("批次檢查訂單商品庫存失敗:", error);
      return {
        allInStock: false,
        outOfStockItems: [],
        details: [],
        error: "檢查庫存時發生錯誤",
      };
    }
  };

  // ==================== 檢查訂單狀態 ====================
  /**
   * 檢查訂單是否為暫存狀態（可重新付款）
   * @param {string} orderNumber - 訂單編號（IofFormId）
   * @returns {Promise<Object>} { isPending: boolean, status: string, statusName: string, message: string, orderData: Object }
   */
  const checkOrderStatus = async (orderNumber) => {
    try {
      if (!orderNumber) {
        return {
          isPending: false,
          status: null,
          statusName: "",
          message: "訂單編號不存在",
          orderData: null,
        };
      }

      // 查詢訂單詳細資料
      const result = await api.productOverviewApi.productOverviewGetOrderGet({
        id: orderNumber,
      });

      if (!result.success || !result.data) {
        return {
          isPending: false,
          status: null,
          statusName: "",
          message: "查詢訂單失敗",
          orderData: null,
        };
      }

      const order = result.data;
      const status = order.IofUseStatus || order.iofUseStatus;
      const statusName = order.IofUseStatusName || order.iofUseStatusName || "";

      // T = 暫存（未付款），A1 = 已付款，B3 = 已取消
      const isPending = status === "T";
      let message = "";

      if (isPending) {
        message = "訂單待付款，可以重新付款";
      } else if (status === "A1") {
        message = "訂單已完成付款";
      } else if (status === "B3") {
        message = "訂單已取消";
      } else {
        message = `訂單狀態：${statusName}`;
      }

      return {
        isPending: isPending,
        status: status,
        statusName: statusName,
        message: message,
        orderData: order,
      };
    } catch (error) {
      console.error("檢查訂單狀態失敗:", error);
      return {
        isPending: false,
        status: null,
        statusName: "",
        message: "檢查訂單狀態時發生錯誤",
        orderData: null,
      };
    }
  };

  // ==================== 綜合驗證訂單可付款性 ====================
  /**
   * 綜合檢查訂單是否可以重新付款（訂單狀態 + 優惠券 + 庫存）
   * @param {string} orderNumber - 訂單編號（IofFormId）
   * @param {Object} options - 檢查選項 { checkCoupon: boolean, checkStock: boolean }
   * @returns {Promise<Object>} { canPay: boolean, errors: Array, warnings: Array, orderData: Object }
   */
  const validateOrderForPayment = async (
    orderNumber,
    options = { checkCoupon: true, checkStock: true },
  ) => {
    try {
      const errors = [];
      const warnings = [];

      // 1. 檢查訂單狀態
      const statusCheck = await checkOrderStatus(orderNumber);
      if (!statusCheck.isPending) {
        errors.push({
          type: "order_status",
          message: statusCheck.message,
        });
        // 訂單狀態錯誤時不需檢查其他項目
        return {
          canPay: false,
          errors: errors,
          warnings: warnings,
          orderData: statusCheck.orderData,
        };
      }

      const orderData = statusCheck.orderData;

      // 2. 檢查優惠券（如果啟用）
      if (
        options.checkCoupon &&
        orderData.Items &&
        orderData.Items.length > 0
      ) {
        const couponCheck = await checkCartCouponsExpiry(
          orderData.Items,
          null,
          false,
        );

        if (!couponCheck.allValid) {
          // 優惠券過期視為警告（可以繼續付款但失去折扣）
          couponCheck.itemExpiredCoupons.forEach((coupon) => {
            warnings.push({
              type: "coupon_expired",
              message: `${coupon.productName} - ${coupon.couponName || coupon.couponCode} 已過期`,
              details: coupon,
            });
          });
        }
      }

      // 3. 後端綜合驗證：超過保留天數（自動取消）、商品庫存不足
      if (options.checkStock) {
        const serverCheck =
          await api.productOverviewApi.productOverviewValidateOrderForPaymentPost(
            { validateOrderForPaymentRequest: { orderNumber } },
          );
        if (serverCheck?.success && serverCheck.data) {
          (serverCheck.data.Errors || []).forEach((err) => {
            errors.push({
              type: (err.Type || "").toLowerCase(),
              message: err.Message,
            });
          });
        }
      }

      return {
        canPay: errors.length === 0,
        errors: errors,
        warnings: warnings,
        orderData: orderData,
      };
    } catch (error) {
      console.error("綜合驗證訂單失敗:", error);
      return {
        canPay: false,
        errors: [
          {
            type: "validation_error",
            message: "驗證訂單時發生錯誤",
          },
        ],
        warnings: [],
        orderData: null,
      };
    }
  };

  return (
    <SearchContext.Provider
      value={{
        updateSearchParams,
        getSearchParams,
        refreshKey,
        triggerRefresh,
        getProductOverviewPath,
        checkTodayUnpaidOrders,
        checkCouponExpiry,
        checkCartCouponsExpiry,
        checkProductStock,
        checkOrderProductsStock,
        checkOrderStatus,
        validateOrderForPayment,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export const useSearchContext = () => useContext(SearchContext);
